/**
 * friendly-error — normalise ANY thrown value (Axios / Error / string / FastAPI
 * 422 detail array) into a short, human, FRENCH message that is safe to show a
 * user. NEVER leaks stack traces, raw "Internal Server Error", SQL, or English
 * technical strings.
 *
 *   import { friendlyError, toFriendlyError, toastError } from '@/lib/friendly-error'
 *
 *   friendlyError(err)                    // => "Une erreur interne est survenue…"
 *   const { title, message } = toFriendlyError(err)
 *   toastError(err)                       // sonner error toast (title + description)
 *   toastError(err, 'Création impossible') // custom fallback title
 *
 * The goal (project DoD): "Always display friendly message for error and other".
 */
import { AxiosError } from 'axios'
import { toast } from 'sonner'

/** Human FR message per HTTP status. */
const STATUS_FR: Record<number, string> = {
  400: 'Requête invalide. Vérifiez les informations saisies.',
  401: 'Votre session a expiré. Veuillez vous reconnecter.',
  402: 'Paiement requis pour accéder à cette fonctionnalité.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'La ressource demandée est introuvable.',
  405: "Cette action n'est pas autorisée ici.",
  408: 'La requête a expiré. Réessayez.',
  409: 'Conflit : cette action entre en conflit avec des données existantes.',
  413: 'Le contenu envoyé est trop volumineux.',
  415: "Ce type de fichier n'est pas pris en charge.",
  422: 'Certaines informations sont invalides ou incomplètes.',
  429: 'Trop de requêtes. Patientez un instant puis réessayez.',
  500: 'Une erreur interne est survenue. Nos équipes ont été notifiées.',
  501: "Cette fonctionnalité n'est pas encore disponible.",
  502: 'Service momentanément indisponible. Réessayez dans un instant.',
  503: 'Service momentanément indisponible. Réessayez dans un instant.',
  504: 'Le service met trop de temps à répondre. Réessayez.',
}

/** Regexes that mark a message as too technical to show a user. */
const TECHNICAL_PATTERNS: RegExp[] = [
  /traceback/i,
  /stack\s*trace/i,
  /sqlalchemy|psycopg|asyncpg|greenlet|integrityerror/i,
  /pydantic|validationerror/i,
  /internal server error/i,
  /nonetype|keyerror|typeerror|valueerror|attributeerror/i,
  /\bundefined\b|\bnull\b|\bNaN\b/,
  /object \w+ has no/i,
  /cannot read propert/i,
  /at line \d+|:\d+:\d+/,
  /<[^>]*error[^>]*>/i,
  /econn|network error|timeout of \d+ms/i,
  /500 internal|502 bad gateway|503 service/i,
]

function looksTechnical(msg: string): boolean {
  if (!msg) return true
  if (msg.length > 240) return true
  return TECHNICAL_PATTERNS.some((re) => re.test(msg))
}

/** A normalised, user-safe error. */
export interface FriendlyError {
  /** Short heading (e.g. "Action impossible"). */
  title: string
  /** Human FR sentence safe to display. */
  message: string
  /** HTTP status when known. */
  status?: number
  /** Machine code when the backend provides one. */
  code?: string
}

/** Extract the most useful backend-provided message, if any & user-safe. */
function extractBackendMessage(data: unknown): string | undefined {
  if (!data) return undefined
  if (typeof data === 'string') return data.trim() || undefined
  if (typeof data !== 'object') return undefined
  const d = data as Record<string, any>

  // FastAPI 422: detail is an array of {loc, msg, type}
  if (Array.isArray(d.detail)) {
    const parts = d.detail
      .map((e: any) => {
        const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : undefined
        const msg = e?.msg || e?.message
        if (!msg) return undefined
        return field && field !== 'body' ? `« ${field} » : ${msg}` : msg
      })
      .filter(Boolean)
    if (parts.length) return parts.slice(0, 3).join(' · ')
  }

  const candidate =
    (typeof d.detail === 'string' && d.detail) ||
    d.message ||
    d.error ||
    d.error_description ||
    d.msg ||
    (d.errors && (Array.isArray(d.errors) ? d.errors.join(' · ') : String(d.errors)))
  return typeof candidate === 'string' ? candidate.trim() || undefined : undefined
}

/**
 * Normalise any thrown value into a { title, message } safe to render.
 * `fallback` overrides the default title AND is used as the message when nothing
 * user-safe can be derived.
 */
export function toFriendlyError(err: unknown, fallback?: string): FriendlyError {
  const defaultTitle = fallback || 'Une erreur est survenue'

  // ── Axios ──────────────────────────────────────────────────────────────────
  if (err instanceof AxiosError || (err as any)?.isAxiosError) {
    const ax = err as AxiosError
    const status = ax.response?.status
    const backendMsg = extractBackendMessage(ax.response?.data)
    const code = (ax.response?.data as any)?.code || ax.code

    // Network / no response
    if (!ax.response) {
      return {
        title: defaultTitle,
        message: 'Impossible de contacter le serveur. Vérifiez votre connexion puis réessayez.',
        code,
      }
    }

    // Prefer a user-safe backend message; else the status template; else generic.
    const statusMsg = status ? STATUS_FR[status] : undefined
    const message =
      (backendMsg && !looksTechnical(backendMsg) ? backendMsg : undefined) ||
      statusMsg ||
      (status && status >= 500
        ? STATUS_FR[500]
        : "L'action n'a pas pu être effectuée. Réessayez.")

    return { title: defaultTitle, message, status, code }
  }

  // ── Plain Error ──────────────────────────────────────────────────────────────
  if (err instanceof Error) {
    const msg = err.message?.trim()
    return {
      title: defaultTitle,
      message: msg && !looksTechnical(msg) ? msg : "L'action n'a pas pu être effectuée. Réessayez.",
    }
  }

  // ── String ─────────────────────────────────────────────────────────────────
  if (typeof err === 'string') {
    return {
      title: defaultTitle,
      message: err && !looksTechnical(err) ? err : (fallback || "L'action n'a pas pu être effectuée. Réessayez."),
    }
  }

  // ── Unknown ────────────────────────────────────────────────────────────────
  return { title: defaultTitle, message: fallback || "L'action n'a pas pu être effectuée. Réessayez." }
}

/** Just the human message string. */
export function friendlyError(err: unknown, fallback?: string): string {
  return toFriendlyError(err, fallback).message
}

/** Show a friendly sonner error toast. Returns the normalised error. */
export function toastError(err: unknown, fallback?: string): FriendlyError {
  const f = toFriendlyError(err, fallback)
  toast.error(f.title, { description: f.message })
  return f
}

/** Convenience success toast (kept here so pages import one error/success helper). */
export function toastSuccess(title: string, description?: string): void {
  toast.success(title, description ? { description } : undefined)
}
